import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, CardContent, Badge } from "@learn-me/ui";
import { CheckCircle, BookOpen, PlayCircle, Clock, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { formatDuration, COURSE_LEVEL_LABELS } from "@learn-me/utils";
import type { ApiResponse } from "@learn-me/types";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { normalizeImageUrl } from "@/lib/imageUrl";

interface CourseWithChapters {
  id: string;
  title: string;
  slug: string;
  thumbnailUrl?: string;
  level?: string;
  chapters?: Array<{
    id: string;
    videoDuration?: number;
  }>;
}

interface LocationState {
  courseId?: string;
  courseSlug?: string;
  orderId?: string;
}

export default function CheckoutSuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | null;

  // Fetch course details if we have a courseId
  const { data: course } = useQuery({
    queryKey: ["course", state?.courseId],
    queryFn: async () => {
      if (!state?.courseSlug && !state?.courseId) return null;
      const slug = state?.courseSlug || state?.courseId;
      const response = await api.get<ApiResponse<CourseWithChapters>>(
        `/courses/${slug}`,
      );
      return response.data.data;
    },
    enabled: !!(state?.courseSlug || state?.courseId),
  });

  // Trigger confetti on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const totalDuration =
    course?.chapters?.reduce(
      (acc: number, ch: { videoDuration?: number }) =>
        acc + (ch.videoDuration || 0),
      0,
    ) || 0;

  const handleShare = async () => {
    if (course && navigator.share) {
      try {
        await navigator.share({
          title: `¡Me inscribí en ${course.title}!`,
          text: `Estoy aprendiendo ${course.title} en ARCUS Learning`,
          url: `${window.location.origin}/courses/${course.slug}`,
        });
      } catch {
        // User cancelled or share failed
      }
    }
  };

  return (
    <div className="py-16">
      <div className="container max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card>
            <CardContent className="py-12">
              {/* Success Icon */}
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </motion.div>

                <h1 className="text-2xl font-bold mb-2">¡Compra Exitosa!</h1>
                <p className="text-muted-foreground">
                  Gracias por tu compra. Ya puedes acceder a tu curso.
                </p>

                {state?.orderId && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Número de orden:{" "}
                    <span className="font-mono">{state.orderId}</span>
                  </p>
                )}
              </div>

              {/* Course Details */}
              {course && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="bg-secondary-50 rounded-lg p-6 mb-8"
                >
                  <div className="flex gap-4">
                    {/* Thumbnail */}
                    <div className="w-32 h-20 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                      {course.thumbnailUrl ? (
                        <img
                          src={normalizeImageUrl(course.thumbnailUrl) ?? ""}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="h-8 w-8 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg line-clamp-1">
                        {course.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <BookOpen className="h-4 w-4" />
                          {course.chapters?.length || 0} capítulos
                        </span>
                        {totalDuration > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {formatDuration(totalDuration)}
                          </span>
                        )}
                      </div>
                      {course.level && (
                        <Badge variant="outline" className="mt-2">
                          {COURSE_LEVEL_LABELS[course.level] ?? course.level}
                        </Badge>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Actions */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col gap-4"
              >
                {/* Primary CTA */}
                <Link to={course ? `/learn/${course.slug}` : "/my-courses"}>
                  <Button size="lg" className="w-full">
                    <PlayCircle className="mr-2 h-5 w-5" />
                    Comenzar a Aprender
                  </Button>
                </Link>

                {/* Secondary Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/my-courses" className="flex-1">
                    <Button variant="outline" size="lg" className="w-full">
                      <BookOpen className="mr-2 h-5 w-5" />
                      Mis Cursos
                    </Button>
                  </Link>
                  {course && typeof navigator.share === "function" && (
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleShare}
                      className="flex-1"
                    >
                      <Share2 className="mr-2 h-5 w-5" />
                      Compartir
                    </Button>
                  )}
                </div>

                {/* Explore more */}
                <Link
                  to="/courses"
                  className="text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Explorar más cursos
                </Link>
              </motion.div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
